import React, { useRef } from "react";
import ReactDOM from "react-dom";
import styles from "./stickyNoteSubMenu.module.css";
import consts from "../shared/consts";
import { Floater } from "./ui-components/floaters/floater";
import { PortalID } from "frontend/modal/portal-types";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import { useFloating, autoUpdate, offset, flip, shift, FloatingPortal, arrow } from "@floating-ui/react";
import classNames from "classnames";
interface StickyNoteSubMenuProps {
  onMenuItemClick: (itemName: { item: string; data: string }) => void;
  relativeTo: React.RefObject<HTMLElement>;
  onHover?: () => void;
  onLeave?: () => void;
}

export default function StickyNoteSubMenu({ onMenuItemClick, relativeTo, onHover, onLeave }: StickyNoteSubMenuProps) {
  const portal = document.getElementById(PortalID.Floaters);
  const isCimReorderEnabled = useIsCIMReorderEnabled();
  const colors = isCimReorderEnabled ? consts.CIM_STICKY_NOTE_COLORS : consts.STICKY_NOTE_COLORS;
  const arrowRef = useRef(null);

  // Set up floating UI
  const { refs, floatingStyles } = useFloating({
    open: isCimReorderEnabled && !!relativeTo.current,
    placement: "top",
    middleware: [offset(15), flip(), shift({ padding: 5 }), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
  });

  // Set the reference element when it changes
  React.useEffect(() => {
    if (relativeTo.current) {
      refs.setReference(relativeTo.current);
    }
  }, [refs]);

  function renderStickyNoteIcon(color: string) {
    if (isCimReorderEnabled) {
      return (
        <div
          className={styles.itemCIMContainer}
          onClick={() => onMenuItemClick && onMenuItemClick({ item: consts.MENU_ITEMS.STICKY_NOTE, data: color })}
        >
          <div style={{ backgroundColor: color }} className={styles.itemCIM} data-color={color}></div>
        </div>
      );
    }
    return (
      <div
        key={color}
        className={styles.item}
        onClick={() => onMenuItemClick && onMenuItemClick({ item: consts.MENU_ITEMS.STICKY_NOTE, data: color })}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="34" height="36" viewBox="0 0 34 36" fill="none">
          <g filter="url(#filter0_f_7169_1873)">
            <path
              d="M29.6322 26.4704H6.29441L5.33203 33.2071L29.8728 31.7346L29.6322 26.4704Z"
              fill="black"
              fillOpacity="0.29"
            />
          </g>
          <g filter="url(#filter1_f_7169_1873)">
            <path
              d="M30.2333 28.2752H3.40697L2.92578 32.7262L32.519 31.1623L30.2333 28.2752Z"
              fill="url(#paint0_linear_7169_1873)"
              fillOpacity="0.4"
            />
          </g>
          <g filter="url(#filter2_f_7169_1873)">
            <path
              d="M30.3531 5.17119L2.44408 4.3291L1.96289 30.3134H32.0373L30.3531 5.17119Z"
              fill="url(#paint1_linear_7169_1873)"
              fillOpacity="0.3"
            />
          </g>
          <path
            d="M1.48317 0H32.7606C32.7606 9.62381 32.3997 16.962 33.0012 31.2774H1.00195C1.48317 14.1951 1.29525 12.2146 1.48317 0Z"
            fill={color}
          />
          <path
            d="M1.48317 0H32.7606C32.7606 9.62381 32.3997 16.962 33.0012 31.2774H1.00195C1.48317 14.1951 1.29525 12.2146 1.48317 0Z"
            fill={color}
          />
          <path
            d="M1.48317 0H32.7606C32.7606 9.62381 32.3997 16.962 33.0012 31.2774H1.00195C1.48317 14.1951 1.29525 12.2146 1.48317 0Z"
            fill={color}
          />
          <path
            d="M1.48317 0H32.7606C32.7606 9.62381 32.3997 16.962 33.0012 31.2774H1.00195C1.48317 14.1951 1.29525 12.2146 1.48317 0Z"
            fill={color}
          />
          <path
            d="M1.48317 0H32.7606C32.7606 9.62381 32.3997 16.962 33.0012 31.2774H1.00195C1.48317 14.1951 1.29525 12.2146 1.48317 0Z"
            fill={color}
          />
          <defs>
            <filter
              id="filter0_f_7169_1873"
              x="2.63978"
              y="23.7781"
              width="29.9255"
              height="12.1211"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
              <feGaussianBlur stdDeviation="1.34613" result="effect1_foregroundBlur_7169_1873" />
            </filter>
            <filter
              id="filter1_f_7169_1873"
              x="1.87879"
              y="27.2282"
              width="31.6877"
              height="6.54497"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
              <feGaussianBlur stdDeviation="0.523494" result="effect1_foregroundBlur_7169_1873" />
            </filter>
            <filter
              id="filter2_f_7169_1873"
              x="0.766332"
              y="3.13254"
              width="32.4673"
              height="28.3774"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
              <feGaussianBlur stdDeviation="0.598279" result="effect1_foregroundBlur_7169_1873" />
            </filter>
            <linearGradient
              id="paint0_linear_7169_1873"
              x1="13.9932"
              y1="29.1173"
              x2="3.64758"
              y2="32.7262"
              gradientUnits="userSpaceOnUse"
            >
              <stop />
              <stop offset="1" stopColor="#202020" stopOpacity="0.36" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_7169_1873"
              x1="16.8798"
              y1="0.720172"
              x2="17.4813"
              y2="34.4035"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopOpacity="0" />
              <stop offset="1" />
            </linearGradient>
            <linearGradient
              id="paint2_linear_7169_1873"
              x1="8.9416"
              y1="-9.98471"
              x2="16.761"
              y2="15.8793"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0.247555" stopColor="#FBF4B5" stopOpacity="0.63" />
              <stop offset="0.459759" stopColor="#EBE0AA" stopOpacity="0.17" />
              <stop offset="0.629337" stopColor="#FFF8B7" stopOpacity="0.22" />
              <stop offset="0.78707" stopColor="#FFF8B7" />
            </linearGradient>
            <radialGradient
              id="paint3_radial_7169_1873"
              cx="0"
              cy="0"
              r="1"
              gradientUnits="userSpaceOnUse"
              gradientTransform="translate(41.3017 36.6908) rotate(-156.598) scale(31.197 54.9297)"
            >
              <stop stopColor="#FFFAC9" />
              <stop offset="1" stopColor="#FFF8B7" stopOpacity="0" />
            </radialGradient>
            <radialGradient
              id="paint4_diamond_7169_1873"
              cx="0"
              cy="0"
              r="1"
              gradientUnits="userSpaceOnUse"
              gradientTransform="translate(32.0387 1.32327) rotate(153.812) scale(8.17759 8.36631)"
            >
              <stop offset="0.576895" stopColor="#F3E9B0" stopOpacity="0.34" />
              <stop offset="1" stopColor="#FFF8B7" stopOpacity="0" />
            </radialGradient>
            <radialGradient
              id="paint5_diamond_7169_1873"
              cx="0"
              cy="0"
              r="1"
              gradientUnits="userSpaceOnUse"
              gradientTransform="translate(24.0991 -2.16536) rotate(156.903) scale(8.89308 9.09832)"
            >
              <stop offset="0.576895" stopColor="#F3E9B0" stopOpacity="0.34" />
              <stop offset="1" stopColor="#FFF8B7" stopOpacity="0" />
            </radialGradient>
          </defs>
        </svg>
        <div className={styles.innerShadow} />
      </div>
    );
  }

  if (isCimReorderEnabled && relativeTo.current) {
    return (
      <FloatingPortal id={PortalID.Floaters}>
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className={classNames(styles.container, styles.containerCIMFooterEnabled)}
          onMouseEnter={onHover}
          onMouseLeave={onLeave}
        >
          <div className={classNames(styles.itemsContainer, styles.itemsContainerCIMFooterEnabled)}>
            {colors.map((color) => {
              return renderStickyNoteIcon(color);
            })}
          </div>
        </div>
      </FloatingPortal>
    );
  }

  const element = relativeTo.current ? (
    <>
      <Floater relativeTo={relativeTo} side={"right"}>
        <div className={styles.container} onMouseOver={onHover} onMouseEnter={onHover} onMouseLeave={onLeave}>
          <div className={styles.itemsContainer}>
            {colors.map((color) => {
              return renderStickyNoteIcon(color);
            })}
          </div>
        </div>
      </Floater>
      <Floater
        relativeTo={relativeTo}
        side={"right"}
        extraStyle={{
          transition: "opacity 0.1s ease-out, transform 0.1s ease-out",
          marginLeft: "-6px",
        }}
      >
        <div className={styles.arrow} />
      </Floater>
    </>
  ) : null;

  return portal ? ReactDOM.createPortal(element, portal) : element;
}
